import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface ActiveStreamData {
  playbackId: string;
  streamTitle: string;
  streamerUsername: string;
  streamId: string;
  isLive: boolean;
  streamerAvatar?: string | null;
}

interface ActiveStreamContextType {
  activeStream: ActiveStreamData | null;
  setActiveStream: (data: ActiveStreamData) => void;
  clearActiveStream: () => void;
  dismissed: boolean;
  dismiss: () => void;
}

const ActiveStreamContext = createContext<ActiveStreamContextType | undefined>(undefined);

export function ActiveStreamProvider({ children }: { children: ReactNode }) {
  const [activeStream, setActiveStreamState] = useState<ActiveStreamData | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const setActiveStream = useCallback((data: ActiveStreamData) => {
    setActiveStreamState(data);
    setDismissed(false);
  }, []);

  const clearActiveStream = useCallback(() => {
    setActiveStreamState(null);
    setDismissed(false);
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  return (
    <ActiveStreamContext.Provider value={{ activeStream, setActiveStream, clearActiveStream, dismissed, dismiss }}>
      {children}
    </ActiveStreamContext.Provider>
  );
}

export function useActiveStream() {
  const ctx = useContext(ActiveStreamContext);
  if (!ctx) throw new Error('useActiveStream must be used within ActiveStreamProvider');
  return ctx;
}
